import { RecipeDesignerIcon } from '../components/Icons/RecipeDesignerIcon';
import { RecipeResultsIcon } from '../components/Icons/RecipeResultsIcon';
import { DigestIcon } from '../components/Icons/DigestIcon';
import { DashboardIcon } from '../components/Icons/DashboardIcon';
import { ForecastIcon } from '../components/Icons/ForecastIcon';
import { PCloseIcon } from '../components/Icons/PCloseIcon';
import { PConvertIcon } from '../components/Icons/PConvertIcon';
import { BoardStatsIcon } from '../components/Icons/BoardStatsIcon';
import { GtmStatsIcon } from '../components/Icons/GtmStatsIcon';
import { OpportunityCreationIcon } from '../components/Icons/OpportunityCreationIcon';
import { BookingTrendsIcon } from '../components/Icons/BookingTrendsIcon';
import { WinLossReasonsIcon } from '../components/Icons/WinLossReasonsIcon';
import { InboundFollowupIcon } from '../components/Icons/InboundFollowupIcon';
import { ActivityHeatmapIcon } from '../components/Icons/ActivityHeatmapIcon';
import { SalesRampIcon } from '../components/Icons/SalesRampIcon';
import { PipelineAnalyticsIcon } from '../components/Icons/PipelineAnalyticsIcon';
import { NodeEffectivenessIcon } from '../components/Icons/NodeEffectivenessIcon';
import { PipelineFlowsIcon } from '../components/Icons/PipelineFlowsIcon';
import { HygieneIcon } from '../components/Icons/HygieneIcon';
import { RenewalMatchingIcon } from '../components/Icons/RenewalMatchingIcon';
import { AccountManagementIcon } from '../components/Icons/AccountManagementIcon';
import { ConfigIcon } from '../components/Icons/ConfigIcon';
import { CustomerInvoicesIcon } from '../components/Icons/CustomerInvoicesIcon';
import { ArrScheduleIcon } from '../components/Icons/ArrScheduleIcon';
import { DepartmentSpendIcon } from '../components/Icons/DepartmentSpendIcon';
import { PromptsIcon } from '../components/Icons/PromptsIcon';
import { MetadataIcon } from '../components/Icons/MetadataIcon';
import { GoalSetsIcon } from '../components/Icons/GoalSetsIcon';
import { MdDesktopMac } from 'react-icons/md';

/**
 * Central route registry mapping all application routes to their metadata.
 * This is the single source of truth for navigation configuration.
 *
 * Note: toolId mappings will be populated by querying the knowledgebase dynamically
 * to find matching UUIDs based on route naming conventions.
 */
const rawRouteRegistry = [
  // Hero Routes - Always visible, recipe-centric features
  {
    id: 'recipe-designer',
    path: '/recipe-designer',
    label: 'Recipe Designer',
    icon: RecipeDesignerIcon,
    category: 'Core',
    tags: ['recipes', 'workflow', 'automation', 'builder'],
    description: 'Build intelligent workflows and automated data pipelines',
    toolId: null, // This is the designer itself, not a specific tool
    relatedTools: [], // Will be populated with all available tools
    dataSource: null,
    requiresSetup: false,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_ops_user', 'general_user'],
    defaultEnabled: true,
    canDisable: false,
    isPermanent: true,
    priority: 1,
  },
  {
    id: 'recipe-results',
    path: '/recipe-results',
    label: 'Recipe Results',
    icon: RecipeResultsIcon,
    category: 'Core',
    tags: ['recipes', 'results', 'executions', 'history'],
    description: 'View execution history and results from recipe runs',
    toolId: null,
    relatedTools: [],
    dataSource: null,
    requiresSetup: false,
    suggestWhen: ['recipe-executed'],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_ops_user', 'general_user', 'finance_user'],
    defaultEnabled: true,
    canDisable: false,
    isPermanent: true,
    priority: 2,
  },

  // Featured Routes - High-value business intelligence
  {
    id: 'business-digest',
    path: '/business-digest',
    label: 'Business Digest',
    icon: DigestIcon,
    category: 'Stats',
    tags: ['digest', 'summary', 'executive', 'overview'],
    description: 'Executive summary of key business metrics and insights',
    toolId: 'business_digest', // Will be mapped to knowledgebase UUID
    relatedTools: ['gtm_stats', 'arr_waterfall'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: [],
    suggestFor: ['admin_user', 'general_user', 'sales_ops_user', 'marketing_ops_user', 'finance_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 3,
  },

  // Predict Category
  {
    id: 'scenarios',
    path: '/scenarios',
    label: 'Scenarios',
    icon: DashboardIcon,
    category: 'Predict',
    tags: ['forecast', 'scenarios', 'planning', 'quota'],
    description: 'Scenario planning and quota allocation modeling',
    toolId: 'scenarios',
    relatedTools: ['forecast', 'pclose'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: ['on-forecast-page'],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_ops_user', 'finance_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 4,
  },
  {
    id: 'forecast',
    path: '/forecast',
    label: 'Forecast',
    icon: ForecastIcon,
    category: 'Predict',
    tags: ['forecast', 'revenue', 'pipeline', 'predictions', 'quota', 'attainment', 'goals'],
    searchAliases: [
      'quota attainment',
      'attainment',
      'sales attainment',
      'rep attainment',
      'sales rep attainment',
      'quota performance',
      'quota progress',
      'quota achievement',
      'goal attainment',
      'forecast attainment',
    ],
    description: 'Revenue forecasting and pipeline analysis',
    tabs: [
      { value: 'detail', label: 'Detail', keywords: ['opportunities', 'deals', 'pipeline breakdown', 'forecast categories'] },
      { value: 'chart', label: 'Deal Roll-up', keywords: ['rollup', 'summary view', 'aggregated forecast'] },
      { value: 'weekly', label: 'Weekly Calls', keywords: ['weekly forecast', 'forecast calls', 'weekly call cadence'] },
      { value: 'history', label: 'History', keywords: ['forecast history', 'past forecasts', 'trend analysis'] },
      {
        value: 'attainment',
        label: 'Attainment',
        keywords: [
          'quota attainment',
          'attainment',
          'sales attainment',
          'rep attainment',
          'sales rep attainment',
          'quota performance',
          'quota progress',
          'quota achievement',
          'quota pacing',
          'quota distribution',
          'quota coverage',
          'goal attainment',
          'goal achievement',
          'forecast attainment',
          'won attainment',
          'commit attainment',
          'expected attainment',
          'upside attainment',
          'bookings against quota',
          'pipeline against quota',
        ],
      },
    ],
    toolId: 'forecast',
    relatedTools: ['pclose', 'scenarios'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: ['on-scenarios-page', 'on-pclose-page'],
    suggestFor: ['admin_user', 'sales_ops_user', 'sales_user', 'marketing_user', 'marketing_ops_user', 'finance_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 5,
  },
  {
    id: 'forecast-setup',
    path: '/forecast-setup',
    label: 'Forecast Setup',
    icon: ConfigIcon,
    category: 'Setup',
    tags: ['forecast', 'setup', 'configuration', 'quota', 'buckets', 'planning'],
    searchAliases: [
      'forecast setup',
      'forecast settings',
      'configure forecast',
      'forecast configuration',
      'forecast bucket definition',
      'quota group definition',
      '/forecast-setup',
    ],
    description: 'Configure forecast buckets, quota roles/groups, and submission settings',
    tabs: [
      { value: '0', label: 'Forecast Period', keywords: ['forecast period', 'period cadence', 'monthly', 'quarterly'] },
      { value: '1', label: 'Forecast Bucket Definition', keywords: ['forecast bucket definition', 'forecast buckets', 'bucket mapping'] },
      { value: '2', label: 'Booking Metric Definition', keywords: ['booking metric', 'booking metric definition', 'arr', 'tcv'] },
      { value: '3', label: 'Quota Role Definition', keywords: ['quota role definition', 'quota roles', 'roles with quota'] },
      { value: '4', label: 'Quota Group Definition', keywords: ['quota group definition', 'quota groups', 'group mapping'] },
      { value: '5', label: 'Split Definition', keywords: ['split definition', 'splits', 'opportunity splits', 'team member splits'] },
      { value: '6', label: 'Submission Reminders', keywords: ['submission reminders', 'forecast reminders', 'weekly reminders'] },
    ],
    toolId: null,
    relatedTools: ['forecast', 'goal_sets'],
    dataSource: 'salesforce',
    requiresSetup: false,
    suggestWhen: ['on-forecast-page'],
    suggestFor: ['admin_user', 'sales_ops_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 5.25,
  },
  {
    id: 'goal-sets',
    path: '/goal-sets',
    label: 'Goal Sets',
    icon: GoalSetsIcon,
    category: 'Predict',
    tags: ['goals', 'quota', 'targets', 'planning'],
    searchAliases: [
      'goal sets',
      'goal set',
      'goalset',
      'goalsets',
      'goals',
      'targets',
      'quota goals',
      'configure goals',
      '/goal-sets',
    ],
    description: 'Goal and quota management for sales teams',
    toolId: 'goal_sets',
    relatedTools: ['forecast', 'scenarios'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: ['on-forecast-page', 'on-scenarios-page'],
    suggestFor: ['admin_user', 'sales_ops_user', 'finance_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 5.5,
  },
  {
    id: 'quota-setup',
    path: '/quota-setup',
    label: 'Quota Setup',
    icon: DashboardIcon,
    category: 'Setup',
    tags: ['quota', 'quotas', 'goals', 'targets', 'planning', 'attainment'],
    searchAliases: [
      'quota setup',
      'quota',
      'quotas',
      'configure quotas',
      'quota config',
      'quota management',
      'quota plan',
      '/quota-setup',
    ],
    description: 'Configure sales quotas and quota roll-ups',
    toolId: null,
    relatedTools: ['forecast', 'goal_sets'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: ['on-forecast-page'],
    suggestFor: ['admin_user', 'sales_ops_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 5.75,
  },
  {
    id: 'pclose',
    path: '/open-pipeline',
    label: 'Open Pipeline',
    icon: PCloseIcon,
    category: 'Predict',
    tags: ['open pipeline', 'pipeline', 'deals', 'opportunities', 'win probability', 'probability', 'close'],
    searchAliases: [
      'open pipeline',
      'deal pipeline',
      'pipeline probability',
      'pipeline health',
      'pclose',
      'p close',
      'p(close)',
      'p close prediction',
      'deal prediction',
      '/pclose',
      '/pclose-v2',
      '/open-pipeline',
    ],
    description: 'Open opportunity pipeline with pivots and win probabilities',
    toolId: 'pclose',
    relatedTools: ['forecast', 'pconvert'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: ['on-forecast-page'],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_user', 'marketing_ops_user', 'finance_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 6,
  },
  {
    id: 'pconvert',
    path: '/new-business-funnel',
    label: 'New Business Funnel',
    icon: PConvertIcon,
    category: 'Stats',
    tags: ['new business funnel', 'convert', 'leads', 'funnel', 'conversion funnel', 'lead funnel', 'mql', 'sal'],
    searchAliases: [
      'new business',
      'new business funnel',
      'funnel',
      'conversion funnel',
      'lead funnel',
      'lead conversion funnel',
      'pconvert',
      'p convert',
      'p(convert)',
      'p convert funnel',
      '/pconvert',
      '/new-business-funnel',
    ],
    description: 'Lead and contact conversion funnel by cohort',
    toolId: 'pconvert',
    relatedTools: ['pclose', 'opp_creation'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: ['on-opp-creation-page'],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_user', 'marketing_ops_user', 'finance_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 7,
  },

  // Stats Category
  {
    id: 'board-stats',
    path: '/board-stats',
    label: 'Board Stats',
    icon: BoardStatsIcon,
    category: 'Stats',
    tags: ['board', 'executive', 'kpi', 'metrics'],
    description: 'Executive KPIs and board-level reporting',
    tabs: [
      { value: 'arr', label: 'ARR', keywords: ['annual recurring revenue', 'waterfall', 'retention', 'expansion', 'churn'] },
      { value: 'revenue', label: 'Revenue', keywords: ['revenue', 'recognized revenue', 'revenue forecast', 'revenue projection', 'topline'] },
      { value: 'booked-billed-recon', label: 'Booked vs Actual', keywords: ['reconciliation', 'variance', 'forecast accuracy'] },
      { value: 'cac', label: 'CAC', keywords: ['customer acquisition cost', 'sales efficiency', 'marketing spend'] },
      { value: 'gross-margin', label: 'Gross Margin', keywords: ['profitability', 'cogs', 'revenue'] },
      { value: 'gtm-efficiency', label: 'GTM Efficiency', keywords: ['go to market', 'sales productivity', 'revenue per rep'] },
      { value: 'magic-number', label: 'Magic Number', keywords: ['sales efficiency', 'arr growth', 'sales and marketing spend'] },
      { value: 'dollar-retention', label: 'Dollar Retention', keywords: ['ndr', 'gdr', 'expansion', 'cohort analysis'] },
      { value: 'net-new-arr', label: 'Net New ARR', keywords: ['growth', 'new business', 'expansion'] },
      { value: 'ltv-cac', label: 'LTV:CAC', keywords: ['lifetime value', 'unit economics', 'payback period'] },
    ],
    toolId: 'arr_waterfall',
    relatedTools: ['gtm_stats', 'business_digest'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: ['recipe-contains-arr_waterfall'],
    suggestFor: ['admin_user', 'finance_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 8,
  },
  {
    id: 'gtm-stats',
    path: '/gtm-stats',
    label: 'GTM Stats',
    icon: GtmStatsIcon,
    category: 'Stats',
    tags: ['gtm', 'sales', 'revenue', 'metrics', 'performance'],
    description: 'Go-to-market performance metrics and trends',
    toolId: 'gtm_stats',
    relatedTools: ['win_rate', 'opp_creation', 'sales_cycle'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: ['recipe-contains-gtm_stats'],
    suggestFor: ['admin_user', 'general_user', 'sales_ops_user', 'marketing_user', 'marketing_ops_user', 'finance_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 9,
  },
  {
    id: 'gtm-stats-segments',
    path: '/gtm-stats-segments',
    label: 'Segment Manager',
    icon: ConfigIcon,
    category: 'Stats',
    tags: ['segments', 'filters', 'query builder', 'view filters', 'custom views', 'opportunity filters'],
    searchAliases: ['segments', 'segment', 'segment manager', 'configure segments', 'segment config'],
    description: 'Create and manage custom segments and filters for GTM analytics',
    toolId: null,
    relatedTools: ['gtm_stats', 'arr_waterfall'],
    dataSource: 'salesforce',
    requiresSetup: false,
    suggestWhen: ['on-gtm-stats-page'],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_ops_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 9.5,
  },
  {
    id: 'opp-creation',
    path: '/opp-creation',
    label: 'Opp Creation',
    icon: OpportunityCreationIcon,
    category: 'Stats',
    tags: ['opportunity', 'creation', 'pipeline', 'conversion'],
    description: 'Opportunity creation patterns and conversion analysis',
    toolId: 'opp_creation',
    relatedTools: ['gtm_stats', 'pconvert'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_user', 'marketing_ops_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 10,
  },
  {
    id: 'win-rate',
    path: '/win-rate',
    label: 'Win Rates',
    icon: BookingTrendsIcon,
    category: 'Stats',
    tags: ['win', 'rate', 'conversion', 'trends'],
    description: 'Win rate trends and booking analysis',
    toolId: 'win_rate',
    relatedTools: ['gtm_stats', 'pipelineflows'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_user', 'marketing_ops_user', 'general_user', 'finance_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 11,
  },
  {
    id: 'win-loss-reasons',
    path: '/win-loss-reasons',
    label: 'Win / Loss Analysis',
    icon: WinLossReasonsIcon,
    category: 'Stats',
    tags: ['win', 'loss', 'reasons', 'analysis'],
    searchAliases: [
      'win loss',
      'win-loss',
      'winloss',
      'loss reasons',
      'closed lost reasons',
      'closed-lost analysis',
    ],
    description: 'Analyze win and loss reasons from deal outcomes',
    toolId: 'win_loss_reasons',
    relatedTools: ['win_rate', 'pclose'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_user', 'marketing_ops_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 12,
  },

  // Sales Drivers Category
  {
    id: 'inbound-follow-up',
    path: '/inbound-follow-up',
    label: 'Inbound Follow-Up',
    icon: InboundFollowupIcon,
    category: 'Sales Drivers',
    tags: ['inbound', 'follow-up', 'response', 'velocity'],
    description: 'Inbound lead response time and follow-up analysis',
    toolId: 'inbound_follow_up',
    relatedTools: ['gtm_activity', 'opp_creation'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_user', 'marketing_ops_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 13,
  },
  {
    id: 'gtm-activity',
    path: '/gtm-activity',
    label: 'GTM Activity',
    icon: ActivityHeatmapIcon,
    category: 'Sales Drivers',
    tags: ['activity', 'engagement', 'touchpoints', 'heatmap'],
    description: 'Sales activity patterns and engagement heatmaps',
    toolId: 'gtm_activity',
    relatedTools: ['inbound_follow_up', 'rep_ramp'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_user', 'marketing_ops_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 14,
  },
  {
    id: 'conversions',
    path: '/conversions',
    label: 'Conversions',
    icon: PipelineFlowsIcon,
    category: 'Sales Drivers',
    tags: ['conversions', 'funnel', 'journey', 'sequence', 'flow', 'conversion filters'],
    description: 'Define and analyze reusable conversion motions across activity, campaign, lifecycle, outcome, and movement events',
    toolId: null,
    relatedTools: ['gtm_activity', 'pipeline_forensics'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: ['on-gtm-activity-page', 'on-pipeline-forensics-page'],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_ops_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 14.25,
  },
  {
    id: 'meetings',
    path: '/meetings',
    label: 'Meetings',
    icon: ActivityHeatmapIcon,
    category: 'Sales Drivers',
    tags: ['meetings', 'calendar', 'activity', 'recordings'],
    description: 'Daily meeting view with owner/invitee tabs and recording indicators',
    toolId: null,
    relatedTools: ['gtm_activity'],
    dataSource: null,
    requiresSetup: true,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'sales_user', 'marketing_user', 'marketing_ops_user', 'finance_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 14.5,
  },
  {
    id: 'rep-ramp',
    path: '/rep-ramp',
    label: 'Rep Ramp',
    icon: SalesRampIcon,
    category: 'Sales Drivers',
    tags: ['ramp', 'sales', 'rep', 'onboarding', 'productivity'],
    description: 'Sales rep ramp time and productivity analysis',
    toolId: 'rep_ramp',
    relatedTools: ['gtm_activity', 'gtm_stats'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_ops_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 15,
  },
  {
    id: 'pipeline-forensics',
    path: '/pipeline-forensics',
    label: 'Pipeline Forensics',
    icon: PipelineAnalyticsIcon,
    category: 'Sales Drivers',
    tags: ['pipeline', 'forensics', 'movement', 'changes'],
    description: 'Deep pipeline movement and stage transition analysis',
    tabs: [
      { value: 'pipeline-climb', label: 'Pipeline Climb', keywords: ['pipeline growth', 'stage progression', 'coverage'] },
      { value: 'calculator', label: 'Conversion Calculator', keywords: ['conversion rates', 'win rate', 'stage conversion'] },
      { value: 'changes', label: 'Coverage', keywords: ['pipeline coverage', 'opportunity mix', 'pipeline health'] },
      { value: 'stage-conversion', label: 'Stage to Stage Stats', keywords: ['stage progression', 'conversion funnel', 'pipeline velocity'] },
      { value: 'balance-sheet', label: 'Balance Sheet', keywords: ['pipeline balance', 'inflow outflow', 'pipeline accounting', 'waterfall', 'pipeline waterfall'] },
    ],
    toolId: 'pipeline_forensics',
    relatedTools: ['forecast', 'pipelineflows'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_user', 'marketing_ops_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 16,
  },

  // Marketing Drivers Category
  {
    id: 'program-effectiveness',
    path: '/program-effectiveness',
    label: 'Program Effectiveness',
    icon: NodeEffectivenessIcon,
    category: 'Marketing Drivers',
    tags: ['marketing', 'programs', 'effectiveness', 'roi'],
    description: 'Marketing program effectiveness and ROI analysis',
    toolId: 'program_effectiveness',
    relatedTools: ['pipelineflows'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_user', 'marketing_ops_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 17,
  },
  {
    id: 'pipelineflows',
    path: '/pipelineflows',
    label: 'Pipeline Attribution',
    icon: PipelineFlowsIcon,
    category: 'Marketing Drivers',
    tags: ['attribution', 'pipeline', 'marketing', 'influence'],
    searchAliases: ['win attribution', 'win flows', 'winflows', 'closed won attribution'],
    description: 'Multi-touch attribution for pipeline influence',
    toolId: 'pipelineflows',
    relatedTools: ['program_effectiveness'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_user', 'marketing_ops_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 19,
  },
  {
    id: 'attribution-setup',
    path: '/attribution-setup/0/true',
    label: 'Attribution Setup',
    icon: ConfigIcon,
    category: 'Marketing Drivers',
    tags: ['setup', 'settings', 'lookback', 'lull', 'configuration'],
    searchAliases: [
      'advanced attribution settings',
      'lookback setup',
      'look-back setup',
      'lull definition',
      'lull setup',
      'marketing attribution settings',
    ],
    description: 'Configure look-back windows and advanced attribution settings',
    toolId: null,
    relatedTools: [],
    routeType: 'configuration',
    dataSource: 'salesforce',
    requiresSetup: false,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_ops_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 19.5,
  },

  // Workflows Category
  {
    id: 'hygiene',
    path: '/hygiene',
    label: 'Data Hygiene',
    icon: HygieneIcon,
    category: 'Workflows',
    tags: ['hygiene', 'data', 'quality', 'cleanup'],
    description: 'Data quality monitoring and hygiene workflows',
    tabs: [
      { value: 'monitoring', label: 'Data Monitoring', keywords: ['data quality', 'monitoring', 'alerts', 'issues', 'anomalies'] },
      { value: 'entity-match', label: 'Entity Match', keywords: ['duplicate detection', 'entity matching', 'deduplication', 'merge'] },
      { value: 'field-integrity', label: 'Field Integrity', keywords: ['field validation', 'data integrity', 'field completeness', 'required fields'] },
      { value: 'ai-insights', label: 'AI Insights', keywords: ['ai analysis', 'insights', 'recommendations', 'data intelligence'] },
    ],
    toolId: 'hygiene',
    relatedTools: [],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_ops_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 20,
  },
  {
    id: 'renewal-matching',
    path: '/renewal-matching',
    label: 'Renewal Matching',
    icon: RenewalMatchingIcon,
    category: 'Workflows',
    tags: ['renewal', 'matching', 'forecasting'],
    description: 'Match renewal opportunities to existing customer contracts',
    toolId: 'renewal_matching',
    relatedTools: ['forecast'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_ops_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 21,
  },
  {
    id: 'account-management',
    path: '/account-management',
    label: 'Account Management',
    icon: AccountManagementIcon,
    category: 'Workflows',
    tags: ['accounts', 'management', 'customers'],
    description: 'Account and customer relationship management',
    toolId: null,
    relatedTools: [],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_ops_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 22,
  },
  {
    id: 'config',
    path: '/config/programs',
    label: 'Campaign Spend',
    icon: ConfigIcon,
    category: 'Workflows',
    tags: ['config', 'campaign', 'spend', 'marketing'],
    description: 'Configure marketing campaign spend and budgets',
    toolId: null,
    relatedTools: ['program_effectiveness'],
    dataSource: null,
    requiresSetup: false,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_user', 'marketing_ops_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 23,
  },
  {
    id: 'customer-invoices',
    path: '/customer-invoices',
    label: 'Customer Invoices',
    icon: CustomerInvoicesIcon,
    category: 'Workflows',
    tags: ['invoices', 'customers', 'billing', 'revenue'],
    description: 'Customer invoice tracking and revenue recognition',
    toolId: null,
    relatedTools: ['arr_schedule'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'finance_user', 'general_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 24,
  },
  {
    id: 'arr-schedule',
    path: '/arr-schedule',
    label: 'ARR Schedule',
    icon: ArrScheduleIcon,
    category: 'Workflows',
    tags: ['arr', 'schedule', 'revenue', 'recurring'],
    description: 'Annual recurring revenue scheduling and tracking',
    toolId: 'arr_schedule',
    relatedTools: ['customer_invoices', 'arr_waterfall'],
    dataSource: 'salesforce',
    requiresSetup: true,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'finance_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 25,
  },
  {
    id: 'department-spend',
    path: '/department-spend',
    label: 'Department Spend',
    icon: DepartmentSpendIcon,
    category: 'Workflows',
    tags: ['department', 'spend', 'budget', 'finance'],
    description: 'Department-level spending and budget management',
    toolId: null,
    relatedTools: [],
    dataSource: null,
    requiresSetup: false,
    suggestWhen: [],
    suggestFor: ['admin_user', 'finance_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 26,
  },
  {
    id: 'manage-prompts',
    path: '/manage-prompts',
    label: 'Prompts',
    icon: PromptsIcon,
    category: 'Workflows',
    tags: ['prompts', 'ai', 'management', 'admin'],
    description: 'Manage AI prompts and templates',
    toolId: null,
    relatedTools: [],
    dataSource: null,
    requiresSetup: false,
    suggestWhen: [],
    suggestFor: ['admin_user'],
    defaultEnabled: false,
    canDisable: true,
    isPermanent: false,
    priority: 27,
  },
  {
    id: 'metadata-setup',
    path: '/metadata-setup',
    label: 'Metadata Setup',
    icon: MetadataIcon,
    category: 'Setup',
    tags: ['metadata', 'fields', 'configuration', 'admin', 'setup'],
    description: 'Configure and manage metadata fields',
    toolId: null,
    relatedTools: [],
    dataSource: null,
    requiresSetup: false,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_ops_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 28,
  },
  {
    id: 'revenue-forecasting',
    path: '/revenue-forecasting',
    label: 'Revenue Forecasting',
    icon: ConfigIcon,
    category: 'Setup',
    tags: ['revenue', 'forecasting', 'actuals', 'accounting', 'drivers', 'recognition'],
    searchAliases: [
      'revenue forecasting',
      'revenue model',
      'revenue modeling',
      'revenue drivers',
      'revenue recognition',
      'income account mapping',
      'accounting revenue mapping',
      '/revenue-forecasting',
    ],
    description: 'Configure revenue actual mappings, revenue drivers, recognition policies, and assumptions',
    toolId: null,
    relatedTools: ['arr_waterfall', 'scenarios'],
    dataSource: 'accounting',
    requiresSetup: false,
    suggestWhen: ['on-board-stats-page'],
    suggestFor: ['admin_user', 'sales_ops_user', 'finance_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 28.5,
  },
  {
    id: 'integrations',
    path: '/admin/company/integrations',
    label: 'Integrations',
    icon: ConfigIcon,
    category: 'Setup',
    tags: ['integrations', 'connections', 'data sources', 'salesforce', 'setup', 'admin', 'configure'],
    searchAliases: [
      'integrations',
      'data sources',
      'connect',
      'connections',
      'salesforce',
      'crm setup',
      '/admin/company/integrations',
    ],
    description: 'Configure and manage data source integrations',
    toolId: null,
    relatedTools: [],
    dataSource: null,
    requiresSetup: false,
    suggestWhen: [],
    suggestFor: ['admin_user'],
    defaultEnabled: true,
    canDisable: false,
    isPermanent: false,
    priority: 29,
  },
  {
    id: 'desktop-tools',
    path: '/desktop-tools',
    label: 'Desktop & AI Tools',
    icon: MdDesktopMac,
    category: 'Setup',
    tags: ['desktop', 'ai', 'integrations', 'tools', 'claude', 'codex', 'mcpb', 'local broker'],
    searchAliases: [
      'desktop',
      'desktop tools',
      'desktop integrations',
      'desktop ai',
      'desktop ai tools',
      'desktop ai integrations',
      'desktop & ai integrations',
      'qflow desktop',
      'claude desktop',
      'claude mcpb',
      'codex desktop',
      'local desktop broker',
      '/desktop-tools',
    ],
    description: 'Download QFlow Desktop, connect Claude and Codex to QFlow, and manage desktop AI integrations',
    tabs: [
      {
        value: 'skills',
        label: 'Organization Skills',
        path: '/desktop-tools/skills',
        keywords: [
          'organization skills',
          'company skills',
          'claude skills',
          'codex skills',
          'skill library',
          'publish skill',
          'skill archives',
        ],
      },
    ],
    toolId: null,
    relatedTools: [],
    dataSource: null,
    requiresSetup: false,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_ops_user', 'general_user', 'sales_user', 'marketing_user', 'finance_user'],
    defaultEnabled: true,
    canDisable: false,
    isPermanent: false,
    priority: 29.5,
  },
  {
    id: 'scenario-settings',
    path: '/scenario-settings',
    label: 'Scenario Settings',
    icon: ConfigIcon,
    category: 'Setup',
    tags: ['scenarios', 'settings', 'configuration', 'planning', 'setup'],
    searchAliases: [
      'scenario settings',
      'scenario config',
      'configure scenarios',
      'scenario setup',
      '/scenario-settings',
    ],
    description: 'Configure scenario planning settings and defaults',
    toolId: null,
    relatedTools: ['scenarios'],
    dataSource: 'salesforce',
    requiresSetup: false,
    suggestWhen: ['on-scenarios-page'],
    suggestFor: ['admin_user', 'sales_ops_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 30,
  },
  {
    id: 'inbound-response-setup',
    path: '/inbound-response-setup',
    label: 'Inbound Response Setup',
    icon: ConfigIcon,
    category: 'Setup',
    tags: ['inbound', 'response', 'setup', 'configuration', 'follow-up', 'sla'],
    searchAliases: [
      'inbound response setup',
      'inbound setup',
      'response time setup',
      'sla setup',
      'follow-up setup',
      '/inbound-response-setup',
    ],
    description: 'Configure inbound response time tracking and SLA settings',
    toolId: null,
    relatedTools: ['inbound_follow_up'],
    dataSource: 'salesforce',
    requiresSetup: false,
    suggestWhen: ['on-inbound-follow-up-page'],
    suggestFor: ['admin_user', 'sales_ops_user'],
    defaultEnabled: true,
    canDisable: true,
    isPermanent: false,
    priority: 31,
  },
  {
    id: 'configure-data',
    path: '/admin/company/configure-data',
    label: 'Configure Data',
    icon: ConfigIcon,
    category: 'Setup',
    tags: ['configure', 'data', 'settings', 'admin', 'setup', 'fields', 'mapping', 'arr', 'opportunity', 'pipeline'],
    searchAliases: [
      'configure data',
      'data config',
      'data settings',
      'field mapping',
      'data setup',
      '/admin/company/configure-data',
    ],
    description: 'Core data configuration and field mappings',
    tabs: [
      { value: '0', label: 'Define Deal Value', keywords: ['arr', 'deal value', 'revenue', 'recurring revenue', 'contract length', 'booking value'] },
      { value: '1', label: 'Map Opportunity Types', keywords: ['opportunity types', 'deal types', 'new business', 'renewal', 'upsell', 'expansion'] },
      { value: '2', label: 'Define Pipeline', keywords: ['pipeline', 'stages', 'opportunity stages', 'forecast categories', 'sales stages'] },
      { value: '3', label: 'Identify Buyer Roles', keywords: ['buyer roles', 'contact roles', 'decision makers', 'stakeholders', 'role hierarchy'] },
      { value: '4', label: 'Describe GTM Programs', keywords: ['gtm programs', 'campaigns', 'marketing programs', 'campaign types', 'task types'] },
      { value: '5', label: 'Map Teams', keywords: ['teams', 'user profiles', 'team mapping', 'sales teams', 'user roles'] },
    ],
    toolId: null,
    relatedTools: [],
    dataSource: 'salesforce',
    requiresSetup: false,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user'],
    defaultEnabled: true,
    canDisable: false,
    isPermanent: false,
    priority: 32,
  },
  {
    id: 'security',
    path: '/security',
    label: 'Security & Privacy',
    icon: ConfigIcon,
    category: 'Setup',
    tags: ['security', 'privacy', 'compliance', 'soc2', 'trust', 'data protection', 'architecture'],
    searchAliases: [
      'security',
      'privacy',
      'soc 2',
      'soc2',
      'compliance',
      'trust center',
      'data protection',
      'confidentiality',
      '/security',
    ],
    description: 'Security, privacy, and architecture overview',
    toolId: null,
    relatedTools: [],
    dataSource: null,
    requiresSetup: false,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_ops_user', 'general_user', 'finance_user'],
    defaultEnabled: true,
    canDisable: false,
    isPermanent: false,
    priority: 33,
  },
  {
    id: 'terms',
    path: '/terms',
    label: 'Terms & Policies',
    icon: ConfigIcon,
    category: 'Setup',
    tags: ['terms', 'service terms', 'privacy policy', 'dpa', 'legal', 'agreements', 'policies'],
    searchAliases: [
      'terms',
      'service terms',
      'privacy policy',
      'dpa',
      'data processing addendum',
      'legal',
      'agreements',
      '/terms',
    ],
    description: 'Service terms, privacy policy, and legal agreements',
    tabs: [
      { value: 'service-terms', label: 'Service Terms', keywords: ['service terms', 'terms of service', 'tos', 'legal terms', 'agreement'] },
      { value: 'privacy-policy', label: 'Privacy Policy', keywords: ['privacy policy', 'privacy', 'data usage', 'personal data', 'gdpr'] },
      { value: 'dpa', label: 'Data Processing Addendum', keywords: ['dpa', 'data processing addendum', 'data processing agreement', 'gdpr', 'data protection'] },
    ],
    toolId: null,
    relatedTools: [],
    dataSource: null,
    requiresSetup: false,
    suggestWhen: [],
    suggestFor: ['admin_user', 'sales_ops_user', 'marketing_ops_user', 'general_user', 'finance_user', 'sales_user', 'marketing_user'],
    defaultEnabled: true,
    canDisable: false,
    isPermanent: false,
    priority: 34,
  },
];

export const routeRegistry = rawRouteRegistry.filter(route => (
  !(route.hiddenInProd && process.env.REACT_APP_STAGE === 'prod')
));

/**
 * Get route definition by ID
 */
export const getRouteById = (routeId) => {
  return routeRegistry.find(route => route.id === routeId);
};

/**
 * Get routes by category
 */
export const getRoutesByCategory = (category) => {
  return routeRegistry.filter(route => route.category === category);
};

/**
 * Get all route categories
 */
export const getCategories = () => {
  return [...new Set(routeRegistry.map(route => route.category))];
};

/**
 * Find route by tool ID
 */
export const findRouteByToolId = (toolId) => {
  return routeRegistry.find(route =>
    route.toolId === toolId || route.relatedTools?.includes(toolId)
  );
};

export default routeRegistry;
